import React, { useCallback, useEffect, useRef, useState } from "react";

import {

  Alert,

  Button,

  Card,

  Col,

  DatePicker,

  Descriptions,

  Input,

  InputNumber,

  Row,

  Space,

  Table,

  Tabs,

  Tag,

  Typography,

  message,

  Spin,
  Select,

} from "antd";

import {

  ThunderboltOutlined,

  HistoryOutlined,

  SaveOutlined,

  SearchOutlined,

  UnorderedListOutlined,

} from "@ant-design/icons";

import dayjs from "dayjs";

import "../css/sm-art-common.css";

import { BASE_URL } from "../services/BaseUrl";

const { Title, Text } = Typography;

const API_BASE = BASE_URL.BASE;

const PAGE_SIZE = 10;

const findField = (obj, fields) => {

  for (const source of [obj?.data, obj?.user, obj]) {

    if (!source || typeof source !== "object") continue;

    for (const field of fields) {

      if (source[field] !== undefined && source[field] !== null && String(source[field]).trim()) {

        return String(source[field]).trim();

      }

    }

  }

  return "";

};

const readUserId = (userdata) => {

  const supplied = findField(userdata, ["su_id", "user_id", "id"]);

  if (supplied) return supplied;

  for (const key of ["userData", "userdata", "user"]) {

    try {

      const stored = JSON.parse(localStorage.getItem(key) || "null");

      const value = findField(stored, ["su_id", "user_id", "id"]);

      if (value) return value;

    } catch {

      // Continue with the next storage key.

    }

  }

  return localStorage.getItem("user_id") || "";

};

const capacityUrl = (unit, action, params = {}) => {

  const safeUnit = String(unit).trim();

  if (!/^\d+$/.test(safeUnit)) throw new Error("A valid unit code is required for capacity APIs.");

  const url = new URL(`/index.php/backlog/${safeUnit}/${action}`, API_BASE);

  Object.entries(params).forEach(([key, value]) => {

    if (value !== undefined && value !== null && value !== "") {

      url.searchParams.set(key, String(value));

    }

  });

  return url.toString();

};

const requestJSON = async (url, options = {}) => {

  const response = await fetch(url, options);

  const json = await response.json().catch(() => null);

  if (!response.ok || json?.status === false || json?.success === false) {

    throw new Error(json?.message || `Request failed (HTTP ${response.status})`);

  }

  if (!json) throw new Error("Server returned an invalid JSON response.");

  return json;

};

const formatValue = (value) => {

  if (value === null || value === undefined || value === "") return "—";

  if (typeof value === "boolean") return String(value);

  if (typeof value === "object") return JSON.stringify(value);

  return String(value);

};

const formatQuantity = (value) => {

  if (value === null || value === undefined || value === "") return "—";

  const number = Number(value);

  return Number.isFinite(number) ? number.toLocaleString("en-IN") : String(value);

};

const CapacityTable = ({ rows, columns, rowKey }) => (

  <div className="sm-table-wrap">

    <Table

      className="sm-table capacity-table"

      rowKey={rowKey}

      columns={columns}

      dataSource={rows}

      pagination={rows.length > PAGE_SIZE ? {

        pageSize: PAGE_SIZE,

        showSizeChanger: false,

        className: "sm-pagination",

        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,

      } : false}

      scroll={{ x: "max-content" }}

      size="middle"

    />

  </div>

);

const DataResult = ({ result, emptyText, kind }) => {

  const data = result?.data;

  if (!result) return <Text type="secondary">{emptyText}</Text>;

  if (!data) return <Alert type="info" showIcon message={result.message || "No data found."} />;

  if (kind === "get") {

    const capacity = data.capacity;

    const topLevelFields = ["unit_name", "date", "entered", "editable"];

    const capacityFields = [

      "id", "unit_name", "capacity_date", "capacity_qty", "remarks",

      "entered_by", "entered_on", "updated_by", "updated_on", "update_count",

    ];

    const label = (field) => field.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

    return (

      <Space direction="vertical" size={16} style={{ width: "100%" }}>

        <Descriptions

          className="capacity-descriptions"

          title="Capacity Details"

          size="small"

          bordered

          column={{ xs: 1, sm: 2, lg: 3 }}

        >

          {topLevelFields.filter((field) => Object.prototype.hasOwnProperty.call(data, field)).map((field) => (

            <Descriptions.Item key={field} label={label(field)}>

              {field === "entered" ? (
                <Tag color={data.entered ? "success" : "default"}>{data.entered ? "Entered" : "Not Entered"}</Tag>
              ) : field === "editable" ? (
                <Tag color={data.editable ? "green" : "default"}>{data.editable ? "Yes" : "No"}</Tag>
              ) : field === "date" ? (
                dayjs(data.date).isValid() ? dayjs(data.date).format("DD-MM-YYYY") : formatValue(data.date)
              ) : formatValue(data[field])}

            </Descriptions.Item>

          ))}

        </Descriptions>

        {data.last_capacity && typeof data.last_capacity === "object" && (
          <div className="capacity-last-record">
            <span className="capacity-last-record-title">Last Entered Capacity</span>
            <div className="capacity-last-record-fields">
              <div className="capacity-last-record-field">
                <span className="capacity-last-record-label">Date</span>
                <strong>{data.last_capacity.date && dayjs(data.last_capacity.date).isValid()
                  ? dayjs(data.last_capacity.date).format("DD-MM-YYYY") : "—"}</strong>
              </div>
              <div className="capacity-last-record-field">
                <span className="capacity-last-record-label">Capacity Qty</span>
                <strong>{formatQuantity(data.last_capacity.capacity_qty)}</strong>
              </div>
            </div>
          </div>
        )}

        <Descriptions

          className="capacity-descriptions"

          title="Capacity Record"

          size="small"

          bordered

          column={{ xs: 1, sm: 2, lg: 3 }}

        >

          {capacity && typeof capacity === "object"

            ? capacityFields.filter((field) => Object.prototype.hasOwnProperty.call(capacity, field)).map((field) => (

                <Descriptions.Item key={field} label={label(field)}>

                  {formatValue(capacity[field])}

                </Descriptions.Item>

              ))

            : <Descriptions.Item label="Capacity">{formatValue(capacity)}</Descriptions.Item>}

        </Descriptions>

      </Space>

    );

  }

  if (kind === "history") {

    const rows = Array.isArray(data.days) ? data.days : [];

    const columns = [

      { title: "Capacity Date", dataIndex: "capacity_date", key: "capacity_date", width: 155, render: formatValue },

      { title: "Capacity Qty", dataIndex: "capacity_qty", key: "capacity_qty", width: 145, align: "right", render: formatValue },

      { title: "Missing", dataIndex: "missing", key: "missing", width: 110, render: (value) => value === undefined ? "—" : formatValue(value) },

      { title: "Remarks", dataIndex: "remarks", key: "remarks", width: 190, render: formatValue },

      { title: "Entered By", dataIndex: "entered_by", key: "entered_by", width: 120, render: formatValue },

      { title: "Entered On", dataIndex: "entered_on", key: "entered_on", width: 190, render: formatValue },

      { title: "Updated By", dataIndex: "updated_by", key: "updated_by", width: 120, render: formatValue },


      { title: "Update Count", dataIndex: "update_count", key: "update_count", width: 130, align: "center", render: formatValue },

    ];

    return (

      <Space direction="vertical" size={14} style={{ width: "100%" }}>

        <Space wrap>

          {data.unit_name && <Tag color="blue">{data.unit_name}</Tag>}

          <Text type="secondary">{formatValue(data.from)} to {formatValue(data.to)}</Text>

        </Space>

        {rows.length ? (

          <CapacityTable rows={rows} columns={columns} rowKey={(row) => row.capacity_date} />

        ) : (

          <Alert type="info" showIcon message="No capacity history returned for the selected range." />

        )}

      </Space>

    );

  }

  if (kind === "log") {

    const rows = Array.isArray(data.log) ? data.log : [];

    const columns = [

      { title: "Action", dataIndex: "action", key: "action", width: 115, render: formatValue },

      { title: "Old Qty", dataIndex: "old_qty", key: "old_qty", width: 125, align: "right", render: formatQuantity },

      { title: "New Qty", dataIndex: "new_qty", key: "new_qty", width: 125, align: "right", render: formatValue },

      { title: "Remarks", dataIndex: "remarks", key: "remarks", width: 190, render: formatValue },

      { title: "Changed By", dataIndex: "changed_by", key: "changed_by", width: 125, render: formatValue },

      { title: "Changed On", dataIndex: "changed_on", key: "changed_on", width: 190, render: formatValue },

      { title: "IP Address", dataIndex: "ip_address", key: "ip_address", width: 155, render: formatValue },

    ];

    return (

      <Space direction="vertical" size={14} style={{ width: "100%" }}>

        <Space wrap><Tag color="blue">{data.unit_name || "Change Log"}</Tag><Text type="secondary">{formatValue(data.date)}</Text></Space>

        {rows.length ? <CapacityTable rows={rows} columns={columns} rowKey={(row, index) => `${row.changed_on || "log"}-${index}`} /> : <Alert showIcon type="info" message="No capacity changes found for this date." />}

      </Space>

    );

  }

  return <Alert type="info" showIcon message={result.message || "No records found."} />;

};

const readUnitCode = (userdata) => {

  const supplied = findField(userdata, ["unit_code"]);

  if (supplied) return supplied;

  for (const key of ["userData", "userdata", "user"]) {

    try {

      const stored = JSON.parse(localStorage.getItem(key) || "null");

      const value = findField(stored, ["unit_code"]);

      if (value) return value;

    } catch {

      // Ignore invalid stored data.

    }

  }

  return localStorage.getItem("unit_code") || "12";

};

// Department 43 = admin. Admins pick the unit from a dropdown.
const readIsAdmin = (userdata) => {
  let dept = findField(userdata, ["dept_ids", "dept", "dept_id", "department_id"]);
  if (!dept) {
    for (const key of ["userData", "userdata", "user"]) {
      try {
        dept = findField(JSON.parse(localStorage.getItem(key) || "null"), ["dept_ids", "dept", "dept_id", "department_id"]);
        if (dept) break;
      } catch {
        // Ignore invalid stored data.
      }
    }
  }
  if (!dept) dept = localStorage.getItem("dept") || localStorage.getItem("dept_id") || "";
  return String(dept).split(/[,\s]+/).includes("43");
};

export default function CapacityManagement({ userdata }) {

  const isAdmin = readIsAdmin(userdata);
  const [selectedUnit, setSelectedUnit] = useState(undefined);
  const [unitOptions, setUnitOptions] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState("");
  const unit = isAdmin ? selectedUnit : readUnitCode(userdata);

  const [date, setDate] = useState(() => dayjs().format("YYYY-MM-DD"));

  const [from, setFrom] = useState(dayjs().subtract(3, "day").format("YYYY-MM-DD"));

  const [to, setTo] = useState(() => dayjs().format("YYYY-MM-DD"));

  const [userId, setUserId] = useState(() => readUserId(userdata));

  const [capacityQty, setCapacityQty] = useState(null);

  const [remarks, setRemarks] = useState("");

  const [activeTab, setActiveTab] = useState("save");

  const [busy, setBusy] = useState({});

  const [errors, setErrors] = useState({});

  const [results, setResults] = useState({});

  const abortControllers = useRef({});

  useEffect(() => {

    if (!userId) setUserId(readUserId(userdata));

  }, [userdata, userId]);

  useEffect(() => () => {

    Object.values(abortControllers.current).forEach((controller) => controller.abort());

  }, []);

  // Admin: load the unit list once (same source as the Style-wise Backlog page).
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      setUnitsLoading(true);
      setUnitsError("");
      try {
        const url = new URL("/index.php/backlog/unit_summary", API_BASE);
        url.searchParams.set("date", dayjs().format("YYYY-MM-DD"));
        const result = await requestJSON(url.toString());
        const items = result?.data?.units;
        if (!Array.isArray(items)) throw new Error("Unit list is missing from the server response.");
        const seen = new Set();
        const options = items.reduce((list, item) => {
          const code = String(item?.unit_code ?? "").trim();
          if (!/^\d+$/.test(code) || seen.has(code)) return list;
          seen.add(code);
          list.push({ value: code, label: `${String(item?.unit_name || `Unit ${code}`).trim()} (${code})` });
          return list;
        }, []).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
        if (!cancelled) setUnitOptions(options);
      } catch (error) {
        if (!cancelled) setUnitsError(error.message || "Unable to load units.");
      } finally {
        if (!cancelled) setUnitsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // When the unit changes, clear results shown for the previous unit.
  useEffect(() => {
    setResults({});
    setErrors({});
  }, [unit]);

  const run = useCallback(async (key, action, options) => {
    if (!unit) {
      setErrors((prev) => ({ ...prev, [key]: "Please select a unit first." }));
      if (key === "save") message.warning("Please select a unit first.");
      return null;
    }

    abortControllers.current[key]?.abort();

    const controller = new AbortController();

    abortControllers.current[key] = controller;

    setBusy((prev) => ({ ...prev, [key]: true }));

    setErrors((prev) => ({ ...prev, [key]: "" }));

    if (key === "save") setResults((prev) => ({ ...prev, save: null }));

    try {

      const result = await requestJSON(capacityUrl(unit, action, options.params), {

        ...(options.fetchOptions || {}),

        signal: controller.signal,

      });

      if (!controller.signal.aborted) {

        setResults((prev) => ({ ...prev, [key]: result }));

        if (key === "save") message.success(result.message || "Capacity saved successfully.");

      }

      return result;

    } catch (error) {

      if (!controller.signal.aborted) {

        setErrors((prev) => ({ ...prev, [key]: error.message || "Request failed." }));

        if (key === "save") message.error(error.message || "Unable to save capacity.");

      }

      return null;

    } finally {

      if (abortControllers.current[key] === controller) {

        setBusy((prev) => ({ ...prev, [key]: false }));

      }

    }

  }, [unit]);

  useEffect(() => {

    if (activeTab === "get") {

      run("get", "get_capacity", { params: { date } });

    } else if (activeTab === "log") {

      run("log", "capacity_log", { params: { date } });

    } else if (activeTab === "history") {

      if (!dayjs(from).isAfter(dayjs(to), "day")) {

        run("history", "capacity_history", { params: { from, to } });

      } else {

        setErrors((prev) => ({ ...prev, history: "From date must be on or before To date." }));

      }

    }

  }, [activeTab, date, from, to, run]);

  const saveCapacity = async () => {

    if (!Number.isFinite(Number(capacityQty)) || capacityQty === null || Number(capacityQty) < 0) {

      return message.warning("Enter a valid capacity quantity.");

    }

    if (!String(userId).trim()) {

      return message.warning("Logged-in user ID is unavailable. Please sign in again.");

    }

    const body = new FormData();

    body.append("capacity_qty", String(capacityQty));

    body.append("date", date);

    body.append("remarks", remarks.trim());

    body.append("user_id", String(userId).trim());

    const saved = await run("save", "save_capacity", {

      fetchOptions: { method: "POST", body },

    });

    if (saved) {

      // Refresh any previously displayed details after saving.

      if (results.get) run("get", "get_capacity", { params: { date } });

      if (results.log) run("log", "capacity_log", { params: { date } });

      if (results.history) run("history", "capacity_history", { params: { from, to } });

    }

  };

  const dateInput = (value, setter) => (

    <DatePicker

      className="sm-date-picker capacity-date-picker"

      value={dayjs(value)}

      allowClear={false}

      format="DD-MM-YYYY"

      onChange={(value) => value && setter(value.format("YYYY-MM-DD"))}

    />

  );

  const renderResult = (key, placeholder) => (

    <Space direction="vertical" size={12} style={{ width: "100%" }}>

      {errors[key] && <Alert type="error" showIcon message={errors[key]} />}

      <Spin spinning={!!busy[key]}>

        <DataResult result={results[key]} emptyText={placeholder} kind={key} />

      </Spin>

    </Space>

  );

  const tabs = [

    {

      key: "save",

      label: <span><SaveOutlined /> Save Capacity</span>,

      children: (

        <Card className="sm-card capacity-panel" title="Save Daily Capacity">

          <Row gutter={[16, 16]}>

            <Col xs={24} sm={12} lg={6}>

              <label className="sm-label">Capacity In Pcs *</label>

              <InputNumber className="sm-input capacity-number-input" style={{ width: "100%", height: 44 }} min={0} precision={0} placeholder="Enter quantity" value={capacityQty} onChange={setCapacityQty} />

            </Col>

            <Col xs={24} sm={12} lg={6}>

              <label className="sm-label">Date *</label>

              {dateInput(date, setDate)}

            </Col>

            <Col xs={24}>

              <label className="sm-label">Remarks</label>

              <Input.TextArea className="capacity-remarks" rows={3} maxLength={1000} showCount value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g., Morning shift capacity" />

            </Col>

            <Col xs={24}>

              <Button className="sm-btn-primary capacity-submit" icon={<SaveOutlined />} loading={!!busy.save} onClick={saveCapacity}>Save Capacity</Button>

            </Col>

          </Row>

          {errors.save && <Alert style={{ marginTop: 16 }} showIcon type="error" message={errors.save} />}

          {results.save && <Alert style={{ marginTop: 16 }} showIcon type="success" message={results.save.message || "Capacity saved successfully"} />}

        </Card>

      ),

    },

    {

      key: "get",

      label: <span><SearchOutlined /> Get Capacity</span>,

      children: (

        <Card className="sm-card capacity-panel" title="Capacity Details">

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>

            <Col xs={24} sm={12} md={7}><label className="sm-label">Date</label>{dateInput(date, setDate)}</Col>

          </Row>

          {renderResult("get", "Capacity details will appear automatically for the selected date.")}

        </Card>

      ),

    },

    {

      key: "history",

      label: <span><HistoryOutlined /> Capacity History</span>,

      children: (

        <Card className="sm-card capacity-panel" title="Capacity History">

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>

            <Col xs={24} sm={12} md={6}><label className="sm-label">From Date</label>{dateInput(from, setFrom)}</Col>

            <Col xs={24} sm={12} md={6}><label className="sm-label">To Date</label>{dateInput(to, setTo)}</Col>

          </Row>

          {renderResult("history", "History will appear automatically for the selected range.")}

        </Card>

      ),

    },

  ];

  return (

    <div className="sm-page capacity-management-page unified-report-page">

      <div className="sm-page-inner">

        <div className="sm-header unified-management-heading">

          <div className="sm-title-wrap">

            <Title level={3} className="sm-page-title">

              <ThunderboltOutlined style={{ color: "#2150FD" }} />

              Capacity Management

            </Title>

            <Text className="sm-page-subtitle">

              Save daily capacity and view capacity details and history.

            </Text>

          </div>

        </div>

        <Card className="sm-card capacity-main-card unified-panel" bordered={false}>

          {isAdmin && (
            <div className="capacity-unit-picker" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
              <label className="sm-label" style={{ margin: 0 }}>Unit *</label>
              <Select
                placeholder="Select Unit"
                showSearch
                optionFilterProp="label"
                style={{ width: 300, maxWidth: "100%" }}
                options={unitOptions}
                loading={unitsLoading}
                disabled={unitsLoading || !!busy.save}
                notFoundContent={unitsError || "No units available"}
                value={selectedUnit}
                onChange={setSelectedUnit}
              />
              {!selectedUnit && <Text type="secondary">Select a unit to save or view its capacity.</Text>}
            </div>
          )}
          {isAdmin && unitsError && <Alert style={{ marginBottom: 12 }} type="error" showIcon message={unitsError} />}
          <Tabs

            className="capacity-tabs"

            activeKey={activeTab}

            onChange={setActiveTab}

            items={tabs}

            destroyInactiveTabPane={false}

          />

        </Card>

      </div>

    </div>

  );

}